/*
 * @Author: Ryan
 * @Date: 2026-02-05
 * @Description: Creative Suite Section - 视频轮播展示，无限循环，中间视频自动播放
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import styles from './index.module.scss';

const ASSET_PREFIX = 'https://assets.falcocut.ai/ai-ugc-video-generator/';

interface VideoItem {
  id: string;
  videoUrl: string;
  posterUrl: string;
}

const originalVideos: VideoItem[] = [
  {
    id: 'cream',
    videoUrl: `${ASSET_PREFIX}ugc-video-use-case-cream.mp4`,
    posterUrl: `${ASSET_PREFIX}ugc-video-use-case-cream-poster.webp`,
  },
  {
    id: 'earbuds',
    videoUrl: `${ASSET_PREFIX}ugc-video-use-case-earbuds.mp4`,
    posterUrl: `${ASSET_PREFIX}ugc-video-use-case-earbuds-poster.webp`,
  },
  {
    id: 'energy-drink',
    videoUrl: `${ASSET_PREFIX}ugc-video-use-case-energy-drink.mp4`,
    posterUrl: `${ASSET_PREFIX}ugc-video-use-case-energy-drink-poster.webp`,
  },
  {
    id: 'product-review',
    videoUrl: `${ASSET_PREFIX}ugc-video-product-review-reaction.mp4`,
    posterUrl: `${ASSET_PREFIX}ugc-video-product-review-reaction-poster.webp`,
  },
  {
    id: 'facemask',
    videoUrl: `${ASSET_PREFIX}ugc-video-use-case-facemask.mp4`,
    posterUrl: `${ASSET_PREFIX}ugc-video-use-case-facemask-poster.webp`,
  },
  {
    id: 'outfit',
    videoUrl: `${ASSET_PREFIX}ugc-video-use-case-outfit.mp4`,
    posterUrl: `${ASSET_PREFIX}ugc-video-use-case-outfit-poster.webp`,
  },
  {
    id: 'perfume',
    videoUrl: `${ASSET_PREFIX}ugc-video-use-case-perfume.mp4`,
    posterUrl: `${ASSET_PREFIX}ugc-video-use-case-perfume-poster.webp`,
  },
];

// 为无限循环创建扩展数组：[...原数组, ...原数组, ...原数组]
// 这样用户始终在中间那份滑动，到达边界时无缝跳转回中间
const videos = [...originalVideos, ...originalVideos, ...originalVideos];
const ORIGINAL_LENGTH = originalVideos.length;
const DEFAULT_INDEX = ORIGINAL_LENGTH + Math.floor(ORIGINAL_LENGTH / 2); // 中间那份的中间位置

// 左箭头图标
const ChevronLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M15 18L9 12L15 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// 右箭头图标
const ChevronRightIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M9 18L15 12L9 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// 静音图标
const MutedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M11 5L6 9H2V15H6L11 19V5Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M23 9L17 15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17 9L23 15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// 有声图标
const UnmutedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M11 5L6 9H2V15H6L11 19V5Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19.07 4.93C20.9447 6.80528 21.9979 9.34836 21.9979 12C21.9979 14.6516 20.9447 17.1947 19.07 19.07"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.54 8.46C16.4774 9.39764 17.004 10.6692 17.004 11.995C17.004 13.3208 16.4774 14.5924 15.54 15.53"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CreativeSuiteSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(DEFAULT_INDEX);
  const [isMuted, setIsMuted] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<Map<number, HTMLVideoElement>>(new Map());
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isJumpingRef = useRef(false);

  // 获取真实视频索引（0-6）
  const getRealIndex = (index: number) => index % ORIGINAL_LENGTH;

  // 滚动到指定索引（不带动画，用于无缝跳转）
  const scrollToIndexInstant = useCallback((index: number) => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const items = container.querySelectorAll(`.${styles.videoItem}`);
    const targetItem = items[index] as HTMLElement;

    if (targetItem) {
      const containerWidth = container.offsetWidth;
      const itemWidth = targetItem.offsetWidth;
      const itemLeft = targetItem.offsetLeft;
      const scrollLeft = itemLeft - (containerWidth - itemWidth) / 2;

      container.scrollTo({
        left: scrollLeft,
        behavior: 'auto',
      });
    }
  }, []);

  // 滚动到指定索引（带动画）
  const scrollToIndex = useCallback((index: number) => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const items = container.querySelectorAll(`.${styles.videoItem}`);
    const targetItem = items[index] as HTMLElement;

    if (targetItem) {
      const containerWidth = container.offsetWidth;
      const itemWidth = targetItem.offsetWidth;
      const itemLeft = targetItem.offsetLeft;
      const scrollLeft = itemLeft - (containerWidth - itemWidth) / 2;

      isScrollingRef.current = true;
      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });

      setTimeout(() => {
        isScrollingRef.current = false;
      }, 500);
    }
  }, []);

  // 播放指定索引的视频
  const playVideoAtIndex = useCallback(
    (index: number) => {
      videoRefs.current.forEach((videoEl, idx) => {
        if (idx === index) {
          videoEl.muted = isMuted;
          videoEl.play().catch(() => {});
        } else {
          videoEl.pause();
          videoEl.currentTime = 0;
        }
      });
    },
    [isMuted],
  );

  // 切换到指定索引
  const goToIndex = useCallback(
    (index: number) => {
      setActiveIndex(index);
      scrollToIndex(index);
      playVideoAtIndex(index);
    },
    [scrollToIndex, playVideoAtIndex],
  );

  // 检查并执行无缝跳转
  const checkAndJump = useCallback(
    (currentIndex: number) => {
      // 如果滑到了第一份，跳转到中间那份的对应位置
      if (currentIndex < ORIGINAL_LENGTH) {
        isJumpingRef.current = true;
        const newIndex = currentIndex + ORIGINAL_LENGTH;
        setActiveIndex(newIndex);
        scrollToIndexInstant(newIndex);
        setTimeout(() => {
          isJumpingRef.current = false;
        }, 50);
      }
      // 如果滑到了第三份，跳转到中间那份的对应位置
      else if (currentIndex >= ORIGINAL_LENGTH * 2) {
        isJumpingRef.current = true;
        const newIndex = currentIndex - ORIGINAL_LENGTH;
        setActiveIndex(newIndex);
        scrollToIndexInstant(newIndex);
        setTimeout(() => {
          isJumpingRef.current = false;
        }, 50);
      }
    },
    [scrollToIndexInstant],
  );

  // 上一个
  const goPrev = useCallback(() => {
    const newIndex = activeIndex - 1;
    goToIndex(newIndex);
    // 延迟检查跳转
    setTimeout(() => checkAndJump(newIndex), 600);
  }, [activeIndex, goToIndex, checkAndJump]);

  // 下一个
  const goNext = useCallback(() => {
    const newIndex = activeIndex + 1;
    goToIndex(newIndex);
    // 延迟检查跳转
    setTimeout(() => checkAndJump(newIndex), 600);
  }, [activeIndex, goToIndex, checkAndJump]);

  // 点击视频
  const handleVideoClick = useCallback(
    (index: number) => {
      if (index !== activeIndex) {
        goToIndex(index);
        setTimeout(() => checkAndJump(index), 600);
      }
    },
    [activeIndex, goToIndex, checkAndJump],
  );

  // 切换静音
  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const newMuted = !prev;
      const videoEl = videoRefs.current.get(activeIndex);
      if (videoEl) {
        videoEl.muted = newMuted;
      }
      return newMuted;
    });
  }, [activeIndex]);

  // 视频播放结束时自动播放下一个
  const handleVideoEnded = useCallback(
    (index: number) => {
      if (index === activeIndex) {
        goNext();
      }
    },
    [activeIndex, goNext],
  );

  // 处理滚动事件
  const handleScroll = useCallback(() => {
    if (isScrollingRef.current || isJumpingRef.current || !containerRef.current) return;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (!containerRef.current || isJumpingRef.current) return;

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;

      const items = container.querySelectorAll(`.${styles.videoItem}`);
      let closestIndex = activeIndex;
      let closestDistance = Infinity;

      items.forEach((item, index) => {
        const itemRect = item.getBoundingClientRect();
        const itemCenter = itemRect.left + itemRect.width / 2;
        const distance = Math.abs(containerCenter - itemCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== activeIndex) {
        setActiveIndex(closestIndex);
        playVideoAtIndex(closestIndex);
        // 检查是否需要跳转
        checkAndJump(closestIndex);
      }
    }, 150);
  }, [activeIndex, playVideoAtIndex, checkAndJump]);

  // 初始化
  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToIndexInstant(DEFAULT_INDEX);
      playVideoAtIndex(DEFAULT_INDEX);
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // 注册 video ref
  const setVideoRef = useCallback(
    (index: number, el: HTMLVideoElement | null) => {
      if (el) {
        videoRefs.current.set(index, el);
        el.onended = () => handleVideoEnded(index);
      } else {
        const existingEl = videoRefs.current.get(index);
        if (existingEl) {
          existingEl.onended = null;
        }
        videoRefs.current.delete(index);
      }
    },
    [handleVideoEnded],
  );

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <h2 className={styles.title}>
          The All-in-One Creative Suite for Modern Commerce
        </h2>
        <p className={styles.description}>
          Empower your marketing team with a versatile set of AI utilities designed to
          accelerate every stage of video production. From impactful social ads to
          top-tier product showcases, our comprehensive toolkit ensures your brand stays
          ahead of the competition without the need for complex software.
        </p>
      </div>

      <div className={styles.carouselWrapper}>
        {/* 左箭头 */}
        <button
          type="button"
          className={`${styles.navButton} ${styles.navButtonLeft}`}
          onClick={goPrev}
          aria-label="Previous video"
        >
          <ChevronLeftIcon />
        </button>

        <div ref={containerRef} className={styles.carousel} onScroll={handleScroll}>
          {videos.map((video, index) => (
            <div
              key={`${video.id}-${index}`}
              className={`${styles.videoItem} ${index === activeIndex ? styles.active : ''}`}
              onClick={() => handleVideoClick(index)}
            >
              <video
                ref={(el) => setVideoRef(index, el)}
                src={video.videoUrl}
                poster={video.posterUrl}
                muted={isMuted}
                playsInline
                preload="metadata"
                className={styles.video}
              />
            </div>
          ))}
        </div>

        {/* 右箭头 */}
        <button
          type="button"
          className={`${styles.navButton} ${styles.navButtonRight}`}
          onClick={goNext}
          aria-label="Next video"
        >
          <ChevronRightIcon />
        </button>

        {/* 静音按钮 */}
        <button
          type="button"
          className={styles.muteButton}
          onClick={toggleMute}
          aria-label={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? <MutedIcon /> : <UnmutedIcon />}
        </button>
      </div>
    </section>
  );
};

export default CreativeSuiteSection;
